import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs.js';
import { Upload, CreditCard, Banknote, ArrowLeftRight, Download, Clock, CheckCircle, XCircle, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const PagosEstudiantes = () => {
  const { user, API } = useContext(AuthContext);
  const [semestres, setSemestres] = useState([]);
  const [misPagos, setMisPagos] = useState([]);
  const [bankInfo, setBankInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Estados para diálogos
  // ELIMINADO: isNewPaymentDialogOpen - Ya no se permite crear nuevos pagos desde aquí
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedPago, setSelectedPago] = useState(null);

  const [paymentForm, setPaymentForm] = useState({
    semestre_id: '',
    moneda: 'USD',
    metodo_pago: 'tarjeta_credito_debito'
  });

  const [uploadForm, setUploadForm] = useState({
    comprobante: null
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar semestres disponibles
      const semestresResponse = await axios.get(`${API}/semestres/precios`);
      setSemestres(semestresResponse.data.semestres || []);

      // Cargar mis pagos/registraciones
      if (['estudiante', 'pastor', 'graduado'].includes(user?.rol)) {
        try {
          const pagosResponse = await axios.get(`${API}/registraciones/mis-registros`);
          setMisPagos(pagosResponse.data.registraciones || []);
        } catch (error) {
          console.log('No previous payments found');
          setMisPagos([]);
        }
      }

      // Cargar información bancaria pública
      const bankResponse = await axios.get(`${API}/bank-info/public`);
      setBankInfo(bankResponse.data.bank_accounts || []);

    } catch (error) {
      toast.error('Error al cargar datos de pagos');
      console.error('Error loading payment data:', error);
    }
    setLoading(false);
  };

  // ELIMINADO: Los pagos nuevos se hacen desde el popup del Dashboard
  // Esta página es SOLO para visualizar historial

  const handleUploadComprobante = async () => {
    if (!uploadForm.comprobante) {
      toast.error('Por favor selecciona un comprobante');
      return;
    }

    setProcessingPayment(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.comprobante);

      await axios.post(`${API}/registraciones/${selectedPago.id}/comprobante`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Comprobante subido exitosamente');
      setIsUploadDialogOpen(false);
      setUploadForm({ comprobante: null });
      loadData(); // Recargar datos

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir comprobante');
      console.error('Upload error:', error);
    }
    setProcessingPayment(false);
  };

  const handleRetryPayment = async (pago) => {
    if (pago.metodo_pago !== 'tarjeta_credito_debito' && pago.metodo_pago !== 'tarjeta_credito') {
      toast.error('Solo se pueden reintentar pagos con tarjeta');
      return;
    }

    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/registraciones/${pago.id}/reintentar-pago`);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reintentar pago');
      console.error('Retry payment error:', error);
    }
    setProcessingPayment(false);
  };

  const getStatusColor = (estado) => {
    switch (estado) {
      case 'aprobado':
      case 'pagado':
        return 'bg-green-100 text-green-800';
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800';
      case 'comprobante_pendiente':
      case 'comprobante_subido':
        return 'bg-blue-100 text-blue-800';
      case 'rechazado':
      case 'cancelado':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (estado) => {
    switch (estado) {
      case 'aprobado':
      case 'pagado':
        return <CheckCircle className="h-4 w-4" />;
      case 'pendiente':
      case 'comprobante_pendiente':
      case 'comprobante_subido':
        return <Clock className="h-4 w-4" />;
      case 'rechazado':
      case 'cancelado':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getMethodIcon = (metodo) => {
    switch (metodo) {
      case 'tarjeta_credito_debito':
      case 'tarjeta_credito':
        return <CreditCard className="h-4 w-4" />;
      case 'efectivo':
      case 'contado':
        return <Banknote className="h-4 w-4" />;
      case 'transferencia_bancaria':
      case 'transferencia':
        return <ArrowLeftRight className="h-4 w-4" />;
      case 'estudiantes_becado':
        return <GraduationCap className="h-4 w-4" />;
      default:
        return <CreditCard className="h-4 w-4" />;
    }
  };

  const getMethodName = (metodo) => {
    switch (metodo) {
      case 'tarjeta_credito_debito':
      case 'tarjeta_credito':
        return 'Tarjeta de Crédito/Débito';
      case 'efectivo':
      case 'contado':
        return 'Efectivo';
      case 'transferencia_bancaria':
      case 'transferencia':
        return 'Transferencia Bancaria';
      case 'estudiantes_becado':
        return 'Estudiantes Becado';
      default:
        return metodo;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Cargando información de pagos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Mis Pagos</h1>
          <p className="text-gray-600 mt-1">Gestiona tus pagos y registraciones de semestres</p>
        </div>
        {/* NOTA: Nuevo pago se hace desde el popup en Dashboard */}
      </div>

      <Tabs defaultValue="mis-pagos" className="w-full">
        <TabsList className="grid w-full grid-cols-1">
          <TabsTrigger value="mis-pagos">Historial de Pagos e Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="mis-pagos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Historial de Pagos</CardTitle>
            </CardHeader>
            <CardContent>
              {misPagos.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No tienes pagos registrados aún</p>
                  {['estudiante', 'pastor', 'graduado'].includes(user?.rol) && (
                    <Button onClick={() => setIsNewPaymentDialogOpen(true)} variant="outline">
                      Realizar primer pago
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {misPagos.map((pago) => (
                    <div key={pago.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{pago.semestre_nombre}</h3>
                          <p className="text-gray-600">
                            {pago.moneda} ${pago.monto_pagado?.toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(pago.estado_pago)}
                          <Badge className={getStatusColor(pago.estado_pago)}>
                            {pago.estado_pago.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            {getMethodIcon(pago.metodo_pago)}
                            <span className="ml-1">{getMethodName(pago.metodo_pago)}</span>
                          </div>
                          <span>
                            {new Date(pago.fecha_registro).toLocaleDateString()}
                          </span>
                        </div>

                        <div className="flex space-x-2">
                          {pago.estado_pago === 'comprobante_pendiente' && (pago.metodo_pago === 'transferencia' || pago.metodo_pago === 'transferencia_bancaria') && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedPago(pago);
                                setIsUploadDialogOpen(true);
                              }}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Upload className="h-4 w-4 mr-1" />
                              Subir Comprobante
                            </Button>
                          )}

                          {pago.estado_pago === 'pendiente' && (pago.metodo_pago === 'tarjeta_credito' || pago.metodo_pago === 'tarjeta_credito_debito') && (
                            <Button
                              size="sm"
                              onClick={() => handleRetryPayment(pago)}
                              disabled={processingPayment}
                              variant="outline"
                            >
                              <CreditCard className="h-4 w-4 mr-1" />
                              Reintentar Pago
                            </Button>
                          )}

                          {pago.recibo_url && (
                            <Button size="sm" variant="outline" onClick={() => window.open(pago.recibo_url, '_blank')}>
                              <Download className="h-4 w-4 mr-1" />
                              Recibo
                            </Button>
                          )}
                        </div>
                      </div>

                      {pago.comentarios_revision && (
                        <div className="mt-3 p-3 bg-gray-100 rounded text-sm">
                          <strong>Comentarios:</strong> {pago.comentarios_revision}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="semestres" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {semestres.map((semestre) => (
              <Card key={semestre.id}>
                <CardHeader>
                  <CardTitle>{semestre.nombre}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{semestre.descripcion}</p>
                  <div className="space-y-2 mb-4">
                    <p><strong>UYU:</strong> ${semestre.precio_uyu?.toLocaleString()}</p>
                    <p><strong>USD:</strong> ${semestre.precio_usd?.toLocaleString()}</p>
                  </div>
                  {['estudiante', 'pastor', 'graduado'].includes(user?.rol) && (
                    <Button 
                      className="w-full"
                      onClick={() => {
                        setPaymentForm({ ...paymentForm, semestre_id: semestre.id });
                        setIsNewPaymentDialogOpen(true);
                      }}
                    >
                      Inscribirse
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog para Nuevo Pago */}
      {/* ELIMINADO: Dialog de nuevo pago - Ahora se hace desde el popup del Dashboard */}

      {/* Dialog para Subir Comprobante */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Subir Comprobante de Transferencia</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="comprobante">Comprobante de Pago</Label>
              <Input
                id="comprobante"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setUploadForm({ comprobante: e.target.files[0] })}
              />
              <p className="text-sm text-gray-500 mt-1">
                Formatos aceptados: PDF, JPG, PNG (máx. 5MB)
              </p>
            </div>

            <div className="flex space-x-2 pt-4">
              <Button variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleUploadComprobante} 
                disabled={processingPayment || !uploadForm.comprobante}
                className="flex-1"
              >
                {processingPayment ? 'Subiendo...' : 'Subir Comprobante'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PagosEstudiantes;